import axios from 'axios';
import { getDeviceId } from '../utils/deviceId';

//const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4005/api/v1';
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Obtiene un listado paginado de animes
 * @param {number} page - Número de página
 * @param {number} limit - Número de animes por página
 * @param {string} searchTerm - Término de búsqueda opcional
 * @returns {Promise<Object>} - Objeto con animes y metadata de paginación
 */
export const getAnimes = async (page = 1, limit = 10, searchTerm = '') => {
  try {
    let url = `${API_URL}/api/v1/animes?page=${page}&limit=${limit}`;
    
    if (searchTerm) {
      url += `&search=${encodeURIComponent(searchTerm)}`;
    }
    
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener animes:', error);
    throw error;
  }
};

/**
 * Obtiene detalles de un anime por su ID
 * @param {number} id - ID del anime
 * @returns {Promise<Object>} - Detalles del anime
 */
export const getAnimeById = async (id) => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/animes/${id}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener anime ID ${id}:`, error);
    throw error;
  }
};

/**
 * Obtiene todas las temporadas de un anime
 * @param {number} animeId - ID del anime
 * @returns {Promise<Array>} - Lista de temporadas
 */
export const getSeasonsByAnime = async (animeId) => {
  try {
    // Usar la ruta correcta del backend: /api/v1/seasons/content/:contentType/:contentId
    const response = await axios.get(`${API_URL}/api/v1/seasons/content/anime/${animeId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener temporadas para anime ID ${animeId}:`, error);
    throw error;
  }
};

/**
 * Obtiene los episodios de una temporada
 * @param {number} seasonId - ID de la temporada
 * @returns {Promise<Array>} - Lista de episodios
 */
export const getEpisodesBySeason = async (seasonId) => {
  try {
    const response = await axios.get(`${API_URL}/api/v1/episodes/season/${seasonId}`);
    return response.data;
  } catch (error) {
    console.error(`Error al obtener episodios para temporada ID ${seasonId}:`, error);
    throw error;
  }
};

/**
 * Obtiene un episodio para reproducción
 * @param {number} episodeId - ID del episodio
 * @returns {Promise<Object>} - Datos del episodio para reproducción
 */
export const getPlayEpisode = async (episodeId) => {
  try {
    // Esta petición necesitará el token de autenticación
    const token = localStorage.getItem('token');
    const deviceId = getDeviceId();
    
    const response = await axios.get(`${API_URL}/api/v1/episodes/play/${episodeId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'user-device-id': deviceId
      }
    });
    return response.data;
  } catch (error) {
    console.error(`Error al obtener episodio para reproducción ID ${episodeId}:`, error);
    throw error;
  }
};

/**
 * Obtiene el último episodio disponible de un anime (primera temporada, primer episodio o el más reciente)
 * @param {number} animeId - ID del anime
 * @returns {Promise<Object>} - Datos del último episodio disponible
 */
export const getLastEpisode = async (animeId) => {
  try {
    // Primero obtenemos todas las temporadas del anime
    const seasonsResponse = await getSeasonsByAnime(animeId);
    
    if (!seasonsResponse.success || !seasonsResponse.data || seasonsResponse.data.length === 0) {
      return { success: false, message: 'No se encontraron temporadas para este anime' };
    }
    
    // Ordenamos las temporadas por número (de menor a mayor)
    const sortedSeasons = [...seasonsResponse.data].sort((a, b) => a.seasonNumber - b.seasonNumber);
    
    // Tomamos la primera temporada para simular comportamiento de Netflix (comienza por el principio)
    const firstSeason = sortedSeasons[0];
    
    // Obtenemos los episodios de esta temporada
    const episodesResponse = await getEpisodesBySeason(firstSeason.id);
    
    if (!episodesResponse.success || !episodesResponse.data || episodesResponse.data.length === 0) {
      return { success: false, message: 'No se encontraron episodios para este anime' };
    }
    
    // Ordenamos los episodios por número (de menor a mayor)
    const sortedEpisodes = [...episodesResponse.data].sort((a, b) => a.episodeNumber - b.episodeNumber);
    
    // Tomamos el primer episodio para comenzar desde el principio
    const firstEpisode = sortedEpisodes[0];
    
    return { 
      success: true, 
      data: firstEpisode,
      message: 'Episodio encontrado'
    };
  } catch (error) {
    console.error(`Error al obtener el último episodio para anime ID ${animeId}:`, error);
    return { 
      success: false, 
      message: 'Error al obtener el último episodio',
      error: error.message 
    };
  }
};

/**
 * Crea un nuevo anime con sus temporadas y episodios (opcional)
 * @param {Object} animeData - Datos del anime y sus temporadas/episodios
 * @returns {Promise<Object>} - Resultado de la creación
 */
export const createAnime = async (animeData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/api/v1/animes/add`, animeData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error al crear anime:', error);
    throw error;
  }
};

/**
 * Previsualiza animes desde un archivo CSV
 * @param {File} file - Archivo CSV
 * @returns {Promise<Object>} - Resultado de la previsualización
 */
export const previewAnimesCsv = async (file) => {
  try {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axios.post(`${API_URL}/api/v1/animes/preview-csv`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error al previsualizar CSV de animes:', error);
    throw error;
  }
};

/**
 * Importa animes desde datos CSV previsualizados
 * @param {Array} animes - Lista de animes a importar
 * @returns {Promise<Object>} - Resultado de la importación
 */
export const importAnimesCsv = async (animes) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(`${API_URL}/api/v1/animes/import-csv`, { animes }, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error al importar animes desde CSV:', error);
    throw error;
  }
};

/**
 * Agrega un episodio a una temporada específica de un anime
 * @param {number} animeId - ID del anime
 * @param {number} seasonNumber - Número de temporada
 * @param {Object} episodeData - Datos del episodio a crear
 * @returns {Promise<Object>} - Resultado de la creación
 */
export const addEpisodeToSeason = async (animeId, seasonNumber, episodeData) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.post(
      `${API_URL}/api/v1/animes/${animeId}/seasons/${seasonNumber}/episodes`, 
      episodeData, 
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        }
      }
    );
    return response.data;
  } catch (error) {
    console.error(`Error al agregar episodio a temporada ${seasonNumber} del anime ${animeId}:`, error);
    throw error;
  }
};

export default {
  getAnimes,
  getAnimeById,
  getSeasonsByAnime,
  getEpisodesBySeason,
  getPlayEpisode,
  getLastEpisode,
  createAnime,
  previewAnimesCsv,
  importAnimesCsv,
  addEpisodeToSeason
};
